import { useCallback, useState } from 'react'

import User from '~/client/src/shared/models/User'
import { IProcoreCompanyWithProjectsAndUsers } from '~/client/src/shared/services/ProcoreService'
import { IServiceUserDto } from '~/client/src/shared/types/UserDto'

import { ConfirmModalKey, PrimaryModalKey } from '../types'

export interface ModalContext {
  newProjectMember: IServiceUserDto | null
  itemsToEdit: IServiceUserDto[]
  pendingInviteUsers: User[]
  pendingApproveUsers: User[]
  pendingSetNotInvitedUsers: User[]
  pendingDeleteUsers: User[]
  procoreCompanies: IProcoreCompanyWithProjectsAndUsers[]
  errorMessage: string
}

export interface ModalState {
  primaryModal: PrimaryModalKey
  confirmModal: ConfirmModalKey
  isAlertOpen: boolean
  context: ModalContext
  openAddUser: (newMember: IServiceUserDto | null) => void
  openEditUsers: (items: IServiceUserDto[]) => void
  openInvite: (users: User[]) => void
  openApprove: (users: User[]) => void
  openSetNotInvited: (users: User[]) => void
  openBulkEdit: () => void
  openDelete: (users: User[]) => void
  openProcore: (companies: IProcoreCompanyWithProjectsAndUsers[]) => void
  openAlert: (errorMessage: string) => void
  close: () => void
  closePrimary: () => void
  closeConfirm: () => void
  closeAlert: () => void
  setError: (errorMessage: string) => void
  resetErrorMessage: () => void
}

const EMPTY_CONTEXT: ModalContext = {
  newProjectMember: null,
  itemsToEdit: [],
  pendingInviteUsers: [],
  pendingApproveUsers: [],
  pendingSetNotInvitedUsers: [],
  pendingDeleteUsers: [],
  procoreCompanies: null,
  errorMessage: '',
}

export function useModalState(): ModalState {
  const [primaryModal, setPrimaryModal] = useState<PrimaryModalKey>(null)
  const [confirmModal, setConfirmModal] = useState<ConfirmModalKey>(null)
  const [isAlertOpen, setIsAlertOpen] = useState(false)
  const [context, setContext] = useState<ModalContext>(EMPTY_CONTEXT)

  const openAddUser = useCallback((newMember: IServiceUserDto | null) => {
    setContext({ ...EMPTY_CONTEXT, newProjectMember: newMember })
    setConfirmModal(null)
    setIsAlertOpen(false)
    setPrimaryModal('addUser')
  }, [])

  const openEditUsers = useCallback((items: IServiceUserDto[]) => {
    setContext({ ...EMPTY_CONTEXT, itemsToEdit: items })
    setConfirmModal(null)
    setIsAlertOpen(false)
    setPrimaryModal('addUser')
  }, [])

  const openInvite = useCallback((users: User[]) => {
    setContext(prev => ({ ...prev, pendingInviteUsers: users }))
    setConfirmModal('invite')
  }, [])

  const openApprove = useCallback((users: User[]) => {
    setContext(prev => ({ ...prev, pendingApproveUsers: users }))
    setConfirmModal('approve')
  }, [])

  const openSetNotInvited = useCallback((users: User[]) => {
    setContext(prev => ({ ...prev, pendingSetNotInvitedUsers: users }))
    setConfirmModal('setNotInvited')
  }, [])

  const openBulkEdit = useCallback(() => {
    setContext(prev => ({ ...prev, errorMessage: '' }))
    setConfirmModal(null)
    setIsAlertOpen(false)
    setPrimaryModal('bulkEdit')
  }, [])

  const openDelete = useCallback((users: User[]) => {
    setContext(prev => ({ ...prev, pendingDeleteUsers: users }))
    setConfirmModal('delete')
  }, [])

  const openProcore = useCallback(
    (companies: IProcoreCompanyWithProjectsAndUsers[]) => {
      setContext({ ...EMPTY_CONTEXT, procoreCompanies: companies })
      setConfirmModal(null)
      setIsAlertOpen(false)
      setPrimaryModal('procore')
    },
    [],
  )

  const openAlert = useCallback((errorMessage: string) => {
    setContext(prev => ({ ...prev, errorMessage }))
    setIsAlertOpen(true)
  }, [])

  const close = useCallback(() => {
    setPrimaryModal(null)
    setConfirmModal(null)
    setIsAlertOpen(false)
    setContext(EMPTY_CONTEXT)
  }, [])

  const closePrimary = useCallback(() => {
    setPrimaryModal(null)
    setConfirmModal(null)
    setContext(EMPTY_CONTEXT)
  }, [])

  const closeConfirm = useCallback(() => {
    setConfirmModal(null)
  }, [])

  const closeAlert = useCallback(() => {
    setIsAlertOpen(false)
    setContext(prev => ({ ...prev, errorMessage: '' }))
  }, [])

  const setError = useCallback((errorMessage: string) => {
    setContext(prev => ({ ...prev, errorMessage }))
  }, [])

  const resetErrorMessage = useCallback(() => {
    setContext(prev => ({ ...prev, errorMessage: '' }))
  }, [])

  return {
    primaryModal,
    confirmModal,
    isAlertOpen,
    context,
    openAddUser,
    openEditUsers,
    openInvite,
    openApprove,
    openSetNotInvited,
    openBulkEdit,
    openDelete,
    openProcore,
    openAlert,
    close,
    closePrimary,
    closeConfirm,
    closeAlert,
    setError,
    resetErrorMessage,
  }
}
